/**
 * Classic 4th-order Runge–Kutta integrator.
 *
 * The SDK has an existing RK45 (adaptive-step) solver in the
 * pharmacokinetics package, but it's intertwined with the PK admin
 * scheduler. For the v0.1 epidemiology engine, a fixed-step RK4 is
 * sufficient — the systems are well-conditioned and step sizes of
 * 0.1-0.5 days give numerical accuracy well within the parameter
 * uncertainty. RK45 with adaptive control is a v0.2 upgrade.
 *
 * Pure-functional. No allocations in the hot loop. Vector-state.
 */

export type Derivatives = (t: number, y: number[]) => number[];

/**
 * Step the system y' = f(t, y) by dt using RK4. Mutates `out` (a
 * caller-supplied scratch buffer of length y.length) and returns it.
 */
export function rk4Step(
  f: Derivatives,
  t: number,
  y: number[],
  dt: number,
  out: number[],
): number[] {
  const n = y.length;
  const k1 = f(t, y);
  const yk2 = new Array<number>(n);
  for (let i = 0; i < n; i++) yk2[i] = y[i]! + (dt / 2) * k1[i]!;
  const k2 = f(t + dt / 2, yk2);
  const yk3 = new Array<number>(n);
  for (let i = 0; i < n; i++) yk3[i] = y[i]! + (dt / 2) * k2[i]!;
  const k3 = f(t + dt / 2, yk3);
  const yk4 = new Array<number>(n);
  for (let i = 0; i < n; i++) yk4[i] = y[i]! + dt * k3[i]!;
  const k4 = f(t + dt, yk4);
  for (let i = 0; i < n; i++) {
    out[i] = y[i]! + (dt / 6) * (k1[i]! + 2 * k2[i]! + 2 * k3[i]! + k4[i]!);
    // Clamp small negatives that can arise from floating-point at near-zero compartments.
    if (out[i]! < 0 && out[i]! > -1e-9) out[i] = 0;
  }
  return out;
}

/**
 * Integrate y' = f(t, y) from t=0 to t=tEnd with fixed step dt.
 * Returns the trajectory: { t: number[]; y: number[][] } where
 * y[i][j] is the j-th compartment at time t[i].
 */
export function integrateRk4(
  f: Derivatives,
  y0: number[],
  tEnd: number,
  dt: number,
): { t: number[]; y: number[][]; steps: number } {
  const n = y0.length;
  const tOut: number[] = [0];
  const yOut: number[][] = [y0.slice()];
  let t = 0;
  let y = y0.slice();
  let next = new Array<number>(n);
  let steps = 0;
  while (t < tEnd - 1e-9) {
    const step = Math.min(dt, tEnd - t);
    rk4Step(f, t, y, step, next);
    t += step;
    tOut.push(t);
    yOut.push(next.slice());
    [y, next] = [next, y];
    steps++;
  }
  return { t: tOut, y: yOut, steps };
}
